<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import SvgButton from "~/components/Common/Buttons/SvgButton.vue";
import { Editor } from "@tiptap/vue-3";

export interface IActions {
  name: string;
  icon: string;
  function: Function;
  label: string;
  level?: number;
  default?: boolean;
  isActive: Function;
}

const props = defineProps({
  name: {
    type: String,
  },
  icon: {
    type: String,
  },
  actions: {
    type: Object as PropType<IActions[]>,
  },
  id: { type: String },
  positionLeft: {
    type: Number,
    default: 0,
  },
  positionTop: {
    type: Number,
    default: 0,
  },
  type: { type: String, default: null },
  editor: Editor,
});

const isPopoverOpen = ref(false);
const buttonRef = ref();
const popoverRef = ref();
const isPopoverContentVisible = ref(false);

async function togglePopover() {
  await nextTick();
  isPopoverOpen.value = !isPopoverOpen.value;
  isPopoverContentVisible.value = !isPopoverContentVisible.value;
}

function closePopover(event: { target: any }) {
  const buttonElement = buttonRef.value?.$el || buttonRef.value;

  if (isPopoverContentVisible.value && event.target && buttonElement && !buttonElement.contains(event.target)) {
    isPopoverContentVisible.value = false;
    isPopoverOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", closePopover);
});

onUnmounted(() => {
  document.removeEventListener("click", closePopover);
});
</script>

<template>
  <div v-if="actions" class="panelButtonActions">
    <SvgButton
      ref="buttonRef"
      class="button-container default-active"
      :name="props.actions?.find((action) => action.isActive())?.icon || actions.find((action) => action.default)?.icon"
      :width="16"
      :height="16"
      @click.stop.prevent="togglePopover"
    >
    </SvgButton>
    <div v-if="isPopoverContentVisible" ref="popoverRef" class="popover-content">
      <ul v-if="type" class="container-actions">
        <li
          v-for="(action, index) in actions"
          :key="index"
          :class="{ 'is-active': editor?.isActive({ [type]: action.name }) }"
        >
          <div
            class="flex items-center"
            @click="
              () => {
                action.function();
              }
            "
          >
            <SvgIcon :name="action.icon" class="svg-basic"></SvgIcon>
          </div>
        </li>
      </ul>
      <ul v-else class="container-actions">
        <li
          v-for="(action, index) in actions"
          :key="index"
          :class="{
            'is-active': action.level
              ? editor?.isActive('heading', { level: action.level })
              : editor?.isActive(action.name),
          }"
        >
          <div
            class="flex items-center"
            @click="
              () => {
                action.function();
              }
            "
          >
            <SvgIcon :name="action.icon" class="svg-basic"></SvgIcon>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.button-container {
  @apply flex-row justify-center items-center rounded-[12px] w-fit;
  height: 36px;
  min-width: 36px;
  cursor: pointer;
  border: 1px solid var(--grey-lightest);
  color: var(--text-secondary-color);
  transition: border-color 0.3s;

  &:hover,
  &:hover * {
    background-color: var(--grey-light);
    border-color: var(--grey-medium-light);
  }

  &:active,
  &:active * {
    border-color: var(--blue-deep-dark);
    color: var(--blue-deep-dark);
  }

  &.default-active {
    border-color: var(--primary-blue-color);
    color: var(--primary-blue-color);
  }

  &.default-active:hover,
  &.default-active:hover * {
    border-color: var(--blue-deep-dark);
    color: var(--blue-deep-dark);
    background-color: var(--grey-light);
  }
}

.svg-basic {
  @apply text-secondary-color;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
}

.icon-svg {
  color: var(--primary-blue-color);
}

.popover-content {
  max-width: 36px;
  width: 36px;
  position: absolute;
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 4px;
  gap: 4px;
  /* background/main */
  background: var(--white-main);
  /* standard-shadow */
  box-shadow: var(--shadow-medium);
  border-radius: 8px;
  z-index: 501;
  top: 45px;
}

.popover-content li {
  padding: 4px;
  cursor: pointer;
}

.panelButtonActions {
  @apply flex-row justify-center items-center cursor-pointer rounded-[12px] w-fit relative;
}

.is-popover-select {
  @apply cursor-pointer;
  background: var(--grey-lightest);
}

.container-actions {
  @apply w-full flex flex-col gap-1 items-center justify-center;
}

.container-actions li {
  @apply cursor-pointer p-1 rounded-lg;

  &:hover {
    background-color: var(--grey-light);
  }

  &.is-active {
    color: var(--primary-blue-color);
  }

  &.is-active * {
    color: var(--primary-blue-color);
  }

  &.is-active:hover,
  &.is-active:hover * {
    color: var(--primary-blue-color);
  }
}
</style>
